<template>
  <div id="common-data-table">
    <template v-if="isCalendarView">
      <v-divider />
      <div class="d-flex align-center">
        <v-btn outlined class="mx-2" @click="calendarModel = null">
          Today
        </v-btn>
        <v-btn icon class="ma-2" @click="$refs.calendar.prev()">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn icon class="ma-2" @click="$refs.calendar.next()">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
        <v-label class="mr-4">{{ currentMonthLabel }}</v-label>
      </div>
      <v-progress-linear v-if="isTableLoading" color="primary" indeterminate />
      <v-calendar
        ref="calendar"
        v-model="calendarModel"
        :type="'month'"
        color="primary"
        :events="parsedEvents"
        @change="updateCalendar"
        @click:day="$emit('add:task', $event)"
        @click:event="editTaskFromCalendarHandler($event.event.taskKey)"
      >
        <template #event="{ event }">
          <v-icon
            v-if="!!event.recurrent_group_id"
            color="white"
            small
            class="ml-1"
          >
            mdi-calendar-sync-outline
          </v-icon>
          <v-icon color="white" small class="mx-1">{{ event.icon }}</v-icon>
          <b>{{ event.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }}</b>
          {{ event.name }}
        </template>
      </v-calendar>
    </template>
    <v-data-table
      v-else
      :headers="headers"
      :items="tasks"
      :sort-by.sync="pagination.sortBy"
      :sort-desc.sync="pagination.descending"
      :items-per-page="itemsPerPage"
      :page="pagination.page"
      :loading="isTableLoading"
      hide-default-header
      hide-default-footer
      :mobile-breakpoint="0"
      item-key="_id"
      class="sc-data-table"
    >
      <template #header="{ props }">
        <tr>
          <th class="px-4">
            <v-checkbox primary hide-details @change="toggleHandler" />
          </th>
          <th />
          <th
            v-for="header in props.headers"
            :key="header.text"
            :class="[
              'column sortable',
              pagination.descending ? 'desc' : 'asc',
              header.value === pagination.sortBy ? 'active' : '',
            ]"
            style="white-space: nowrap"
            @click="changeSort(header.value)"
          >
            {{ header.text }}
            <v-icon v-if="header.value === pagination.sortBy" small>
              mdi-arrow-up
            </v-icon>
          </th>
        </tr>
      </template>
      <template #item="{ item }">
        <TaskTableContentItem
          :item="item"
          :is-selected="selected.includes(item._key)"
          @edit:task="editTaskHandler(item)"
          @select:task="selectItemHandler"
        />
      </template>
      <template #footer>
        <div class="d-flex">
          <v-spacer />
          <div class="d-flex align-center">
            <span class="mr-2">Per page: </span>
            <v-select
              v-model="itemsPerPage"
              :items="pagination.itemsPerPageOptions"
              hide-details
              :disabled="isTableLoading"
              dense
              class="pagination-select"
              @change="perPageChangeHandler"
            />
          </div>
          <v-pagination
            v-model="pagination.page"
            :length="pagesLength"
            :disabled="isTableLoading"
            :total-visible="$vuetify.breakpoint.xs ? 3 : 5"
          />
        </div>
      </template>
    </v-data-table>
    <EditTaskForm
      v-model="editTaskDialog"
      :associated-records="associatedRecords"
      :task-entry="taskToEdit"
      :collection-type="collectionType"
      @submit="$emit('update:task', pagination)"
      @fetch:tasks="getTasks"
      @close="taskToEdit = null"
    />
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import { mapMutations, mapState } from 'vuex';
import { cloneDeep } from 'lodash-es';
import { DateTime } from 'luxon';

import { ITask, ITaskForCalendar } from '@/bundles/Tasks/interfaces/ITask';
import { getTasksTableHeaders } from '@/bundles/Tasks/helpers/tableHeaders';
import { IHeader } from '@/bundles/BaseTable/interfaces';
import { getDefaultTaskPagination, ITasksTablePagination } from '@/bundles/Tasks/helpers/defaultPagination';
import { SET_TASK_TABLE_PER_PAGE } from '@/store/modules/task/types';
import { Nullable } from '@/utils/types';
import { getTaskColorByAssignee, getTaskIconByType } from '@/bundles/Tasks/helpers';

import TaskTableContentItem from '@/bundles/Tasks/components/table/TaskTableContentItem.vue';
import EditTaskForm from '@/bundles/Tasks/components/EditTaskForm.vue';

interface IEvent {
  name: string;
  start: Date;
  end: Date;
  color: string;
  timed: boolean;
  taskKey: string;
  icon?: string;
  recurrent_group_id: Nullable<string>;
}

interface ITasksTable {
  pagination: ITasksTablePagination;
  editTaskDialog: boolean;
  taskToEdit: Nullable<ITask>;
  calendarModel: Nullable<string>;
  parsedEvents: IEvent[];
}

export default Vue.extend({
  name: 'TasksTable',

  components: { EditTaskForm, TaskTableContentItem },

  props: {
    tasks: {
      type: Array as PropType<Array<ITask | ITaskForCalendar>>,
      default: () => [],
    },
    totalRows: {
      type: Number,
      required: true,
    },
    collectionType: {
      type: String,
      default: null
    },
    selected: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    associatedRecords: {
      type: Array,
      default: () => []
    },
    isTableLoading: Boolean,
    isCalendarView: Boolean,
    calendarDateStart: {
      type: String, // iso date
      default: null,
    },
    calendarDateEnd: {
      type: String, // iso date
      default: null,
    }
  },

  data: (): ITasksTable => ({
    pagination: getDefaultTaskPagination(),
    editTaskDialog: false,
    taskToEdit: null,
    calendarModel: null,
    parsedEvents: [],
  }),

  computed: {
    ...mapState('Task', {
      tasksItemsPerPage: (state: any): number => state.tableOptions.itemsPerPage,
    }),
    headers (): IHeader[] {
      return getTasksTableHeaders();
    },
    pagesLength (): number {
      if (!this.totalRows) {
        return 1;
      }

      return Math.ceil(this.totalRows / this.itemsPerPage);
    },
    itemsPerPage: {
      get (): number {
        return this.tasksItemsPerPage;
      },
      set (value: number) {
        this.setTaskTablePerPage(value);
      },
    },
    currentMonthLabel (): string {
      if (!this.isCalendarView) return '';

      const date = this.calendarModel ? DateTime.fromISO(this.calendarModel) : DateTime.now();
      return date.toFormat('LLLL y');
    },
  },

  watch: {
    'pagination.page' () {
      this.paginationChangeHandler();
    },
    'pagination.sortBy' () {
      this.paginationChangeHandler();
    },
    'pagination.descending' () {
      this.paginationChangeHandler();
    },
    tasks: {
      handler () {
        this.parseEvents();
      },
      deep: true,
      immediate: true,
    }
  },

  methods: {
    ...mapMutations('Task', {
      setTaskTablePerPage: SET_TASK_TABLE_PER_PAGE,
    }),
    perPageChangeHandler () {
      const isFirstPage = this.pagination.page === 1;

      this.pagination.page = 1;

      if (isFirstPage) {
        this.paginationChangeHandler();
      }
    },
    paginationChangeHandler () {
      this.getTasks(true);
    },
    getTasks (isPaginationChanged = false) {
      this.$emit('fetch:tasks', { isPaginationChanged, pagination: this.pagination });
    },
    toggleHandler (value: boolean) {
      const selectedArr = value ? this.tasks.map((item) => item._key) : [];
      this.selectTasksHandler(selectedArr);
    },
    changeSort (column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },
    selectItemHandler (taskId) {
      const selectedArr = this.selected.includes(taskId)
          ? this.selected.filter((key) => key !== taskId)
          : [...this.selected, taskId];
      this.selectTasksHandler(selectedArr);
    },
    selectTasksHandler (selected: string[]) {
      this.$emit('select:tasks', selected);
    },
    editTaskFromCalendarHandler (taskKey: string) {
      this.editTaskHandler(this.tasks.find((t) => t._key === taskKey)!);
    },
    editTaskHandler (task: ITask) {
      this.taskToEdit = cloneDeep(task);
      this.editTaskDialog = true;
    },
    updateCalendar ({ start, end }) { // iso dates
      const viewStart = (this.$refs.calendar as any).getStartOfWeek(start);
      const viewEnd = (this.$refs.calendar as any).getEndOfWeek(end);

      if (this.calendarDateStart === viewStart.date && this.calendarDateEnd === viewEnd.date) {
        return; // No change in calendar view
      }

      this.$emit('update:calendarDateStart', viewStart.date);
      this.$emit('update:calendarDateEnd', viewEnd.date);
      this.$emit('fetch:tasks');
    },
    parseEvents () {
      if (!this.isCalendarView) return;

      const events: IEvent[] = [];

      this.tasks.forEach((task) => {
        const startEvent = new Date(task.due_date);
        const endEvent = new Date(startEvent);

        events.push({
          icon: getTaskIconByType(task.type),
          name: task.title,
          start: startEvent,
          end: endEvent,
          color: getTaskColorByAssignee((task as ITaskForCalendar).assignee_privacy),
          timed: true,
          taskKey: task._key,
          recurrent_group_id: task.recurrent_group_id || null,
        });
      });

      this.parsedEvents = events;
    }
  }
});
</script>

<style lang="scss">
.pagination-select {
  margin-left: 8px;

  .v-select__selections {
    padding-left: 2px;

    input {
      display: none;
    }
  }
}
</style>
